/**
 * Contratto REST del portale (QA26-F4.x): DTO di risposta e input validati con zod. Il frontend (N3) e gli strumenti di
 * MCP e Assistente (N4) leggono da qui. Importi in numero (EUR salvo `valuta`), date ISO (YYYY-MM-DD o timestamp).
 */
import { z } from "zod";
import {
  ESITI_VERIFICA,
  FASI_NC,
  LISTE,
  SEVERITA,
  STATI_CLAIM,
  STATI_PROPOSTA,
  STATI_AZIONE,
  STATI_SEGNALAZIONE,
  TIPI_AZIONE,
  TIPI_CLASSIFICAZIONE,
  TIPI_NC,
  TIPI_PROPOSTA,
  VALUTE,
  type Azione,
  type EsitoVerifica,
  type FaseNc,
  type FlagSettimana,
  type Lista,
  type RuoloPortale,
  type SezioneRuoli,
  type Severita,
  type StatoClaim,
  type StatoNc,
  type StatoProposta,
  type StatoAzione,
  type StatoSegnalazione,
  type TipoAllegato,
  type TipoAzione,
  type TipoClassificazione,
  type TipoNc,
  type TipoProposta,
  type Valuta,
} from "./dominio";
import type { StatoPro09 } from "./calendario";

export interface ErrorDto {
  error: { code: string; message: string; key?: string; params?: Record<string, string | number>; details?: unknown };
}

/* ---------- utente ---------- */

export interface MeDto {
  id: string;
  email: string;
  nome: string;
  /** ruoli del portale, uno per sezione (NCI26-F3.0) */
  ruoli: RuoloPortale[];
  /** permessi: unione dei permessi dei ruoli (permessiDi in dominio.ts) */
  azioni: Azione[];
  sso: boolean;
}
export interface UtenteBreveDto {
  id: string;
  nome: string;
  email: string;
}
export interface OwnerDto {
  /** nome come scritto (utente del portale o testo libero) */
  nome: string | null;
  utenteId: string | null;
}

/* ---------- liste ---------- */

export interface VoceListaDto {
  id: string;
  lista: Lista;
  valore: string;
  ordine: number;
  attiva: boolean;
}
export interface CriterioSeveritaDto {
  severita: Severita;
  testo: string;
}
export interface ListeDto {
  voci: VoceListaDto[];
  criteri: CriterioSeveritaDto[];
  cambioUsdEur: number;
}

/* ---------- fornitori ---------- */

export interface FornitoreBreveDto {
  id: string;
  nome: string;
  codice: string | null;
  paese: string | null;
}
export interface FornitoreDto extends FornitoreBreveDto {
  nomeLocale: string | null;
  partitaIva: string | null;
  indirizzo: string | null;
  statoVendorhub: string | null;
  stabilimenti: { id: string; nome: string; codice: string | null; citta: string | null; paese: string | null }[];
  /** rating letto dal VendorHub: non c'è nel dump, resta null in v1 */
  rating: string | null;
  segnalazioni: SegnalazioneRigaDto[];
  nc: NcRigaDto[];
  claim: ClaimRigaDto[];
  /** NC da Audit Plan: in arrivo (null = non collegato) */
  ncAudit: number | null;
}

/* ---------- segnalazioni ---------- */

export interface ClassificazioneDto {
  /** tipo di segnalazione proposto o approvato (lista tipo_segnalazione) */
  tipoSegnalazione: string | null;
  /** codici della classificazione della notte (storico: non aprono più NC) */
  tipi: TipoClassificazione[];
  causaCliente: string | null;
  causaChi: string | null;
  fornitore: FornitoreBreveDto | null;
  /** testo del fornitore come nel file (quando non abbinato al VendorHub) */
  fornitoreTesto: string | null;
}
export interface PropostaBreveDto {
  id: string;
  codice: string;
  stato: StatoProposta;
  propostaDa: string;
}
export interface SegnalazioneRigaDto {
  id: string;
  codice: string;
  societa: string;
  cliente: string;
  progetto: string | null;
  dataTesto: string | null;
  dataSegnalazione: string | null;
  dataOrdinamento: string | null;
  severita: Severita;
  urgente: boolean;
  valoreTesto: string | null;
  valoreImporto: number | null;
  valuta: Valuta | null;
  valoreEur: number | null;
  stato: StatoSegnalazione;
  flag: FlagSettimana | null;
  settimanaFlag: string | null;
  /** classificazione approvata (null se non ancora approvata) */
  classificazione: ClassificazioneDto | null;
  /** ultima classificazione proposta e in attesa dell'admin */
  classificazioneProposta: (ClassificazioneDto & { proposta: PropostaBreveDto }) | null;
  ncAperte: number;
  ncTotali: number;
  ownerQualita: OwnerDto;
  ownerCommerciale: OwnerDto;
  /** fonte (lista «fonte»): mail, telefono, Salesforce, ... */
  fonte: string | null;
  /** tipo di segnalazione (lista «tipo_segnalazione») */
  tipoSegnalazione: string | null;
  /** cliente nella lista dei clienti VIP (PRO 09: aggiornamento ogni 2 giorni) */
  vip: boolean;
  /** prossimo aggiornamento dovuto al cliente (PRO 09); null se non aperta */
  pro09: StatoPro09 | null;
  azioniAperte: number;
  azioniScadute: number;
  presaInCarico: boolean;
}
export interface AggiornamentoDto {
  id: string;
  at: string;
  autore: string;
  tipo: string;
  testo: string;
}
export interface SegnalazioneDto extends SegnalazioneRigaDto {
  problema: string | null;
  causa: string | null;
  impatto: string | null;
  azioneInCorso: string | null;
  /** dettaglio della fonte (chi ha segnalato, riferimento) */
  fonteDettaglio: string | null;
  /** giorno di ricezione: da qui partono i tempi della PRO 09 */
  ricevutaIl: string;
  presaInCaricoIl: string | null;
  presaInCaricoDa: string | null;
  ultimoAggiornamentoCliente: string | null;
  chiusaIl: string | null;
  verificataConCliente: boolean;
  creataDa: string | null;
  casoSalesforce: string | null;
  poSap: string | null;
  costoNonQualita: number | null;
  nc: NcRigaDto[];
  /** claim collegati (vuoto per chi non vede i claim) */
  claim: ClaimRigaDto[];
  proposte: PropostaRigaDto[];
  aggiornamenti: AggiornamentoDto[];
  azioni: AzioneDto[];
  allegati: AllegatoDto[];
  /** passo del processo per la barra: 1 segnalata, 2 presa in carico, 3 azioni in corso, 4 chiusa */
  passo: 1 | 2 | 3 | 4;
  /** cosa può fare chi guarda (oltre ai permessi del ruolo) */
  puo: { modificare: boolean; allegare: boolean; creareAzioni: boolean; aprireNc: boolean };
}

/* ---------- azioni ISO 9001 (QA26-F4.10) ---------- */

export interface AzioneDto {
  id: string;
  codice: string;
  segnalazione: { id: string; codice: string; cliente: string; severita: Severita };
  tipo: TipoAzione;
  descrizione: string;
  funzione: string | null;
  owner: OwnerDto;
  scadenza: string | null;
  stato: StatoAzione;
  scaduta: boolean;
  creataDa: string;
  creataIl: string;
  verificataDa: string | null;
  verificataIl: string | null;
  aggiornamenti: AggiornamentoDto[];
  /** chi guarda può aggiornarla (owner, o chi modifica la segnalazione) */
  aggiornabile: boolean;
  /** chi guarda può metterla «verificata» (qualità, admin) */
  verificabile: boolean;
}

export const FiltriAzioniIn = z.object({
  mie: z.coerce.boolean().optional(),
  aperte: z.coerce.boolean().optional(),
  scadute: z.coerce.boolean().optional(),
  stato: z.enum(STATI_AZIONE).optional(),
  tipo: z.enum(TIPI_AZIONE).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FiltriAzioni = z.infer<typeof FiltriAzioniIn>;

/* ---------- allegati (QA26-F4.9) ---------- */

export interface MailAnteprimaDto {
  da: string | null;
  a: string | null;
  cc: string | null;
  data: string | null;
  oggetto: string | null;
  /** corpo in testo semplice (troncato a 100.000 caratteri) */
  corpo: string | null;
  /** allegati della mail non estratti (formato non ammesso, troppo grandi, messaggi incorporati) */
  nonEstratti: { nome: string; motivo: string }[];
}
export interface AllegatoDto {
  id: string;
  nome: string;
  tipo: TipoAllegato;
  contentType: string;
  dimensione: number;
  caricatoDa: string;
  at: string;
  /** allegato estratto da una mail: id della mail */
  padreId: string | null;
  mail: MailAnteprimaDto | null;
  /** immagini e PDF si aprono nel portale */
  anteprima: boolean;
}
export interface PaginaDto<T> {
  righe: T[];
  totale: number;
}

const testo = (max = 5000) => z.string().trim().max(max);
const testoOpz = (max = 5000) => testo(max).nullish();
const importo = z.number().min(0).max(1e12).nullish();
const data = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data AAAA-MM-GG");
const ownerIn = z.object({ nome: testoOpz(200), utenteId: z.string().min(1).nullish() });

export const FiltriSegnalazioniIn = z.object({
  q: z.string().trim().max(200).optional(),
  severita: z.enum(SEVERITA).optional(),
  stato: z.enum(STATI_SEGNALAZIONE).optional(),
  societa: z.string().trim().max(100).optional(),
  tipo: z.enum(TIPI_CLASSIFICAZIONE).optional(),
  tipoSegnalazione: z.string().trim().max(100).optional(),
  fonte: z.string().trim().max(100).optional(),
  inRitardo: z.coerce.boolean().optional(),
  vip: z.coerce.boolean().optional(),
  urgenti: z.coerce.boolean().optional(),
  aperte: z.coerce.boolean().optional(),
  fornitoreId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FiltriSegnalazioni = z.infer<typeof FiltriSegnalazioniIn>;

export const NuovaSegnalazioneIn = z.object({
  fonte: testo(100).min(1),
  fonteDettaglio: testoOpz(300),
  tipoSegnalazione: testoOpz(100),
  societa: testo(100).min(1),
  cliente: testo(200).min(1),
  progetto: testoOpz(300),
  problema: testo().min(1),
  impatto: testoOpz(),
  severita: z.enum(SEVERITA),
  urgente: z.boolean().default(false),
  valoreImporto: importo,
  valuta: z.enum(VALUTE).default("EUR"),
  casoSalesforce: testoOpz(40),
  poSap: testoOpz(40),
  dataSegnalazione: data.optional(),
});
export type NuovaSegnalazioneInput = z.input<typeof NuovaSegnalazioneIn>;

export const ModificaSegnalazioneIn = z
  .object({
    societa: testo(100).min(1),
    cliente: testo(200).min(1),
    progetto: testoOpz(300),
    problema: testoOpz(),
    causa: testoOpz(),
    impatto: testoOpz(),
    azioneInCorso: testoOpz(),
    fonte: testo(100).min(1),
    fonteDettaglio: testoOpz(300),
    tipoSegnalazione: testoOpz(100),
    severita: z.enum(SEVERITA),
    urgente: z.boolean(),
    valoreImporto: importo,
    valuta: z.enum(VALUTE),
    stato: z.enum(STATI_SEGNALAZIONE),
    casoSalesforce: testoOpz(40),
    poSap: testoOpz(40),
    ownerQualita: ownerIn,
    ownerCommerciale: ownerIn,
    /** obbligatorio per chiudere: la chiusura avviene dopo la verifica col cliente (PRO 09) */
    verificataConCliente: z.boolean(),
  })
  .partial();
export type ModificaSegnalazioneInput = z.input<typeof ModificaSegnalazioneIn>;

export const AggiornamentoIn = z.object({ testo: testo(4000).min(1) });
/** Aggiornamento dato al cliente (PRO 09): riparte il conteggio. */
export const AggiornamentoClienteIn = z.object({ testo: testo(4000).min(1), data: data.optional() });
/** «Apri NC da questa segnalazione» (qualità, admin): la NC resta collegata. */
export const ApriNcIn = z.object({
  tipo: z.enum(TIPI_NC),
  fornitoreId: z.string().min(1).nullish(),
  fornitoreTesto: testoOpz(200),
  funzione: testoOpz(100),
});
export type ApriNcInput = z.input<typeof ApriNcIn>;
export const NuovaAzioneIn = z.object({
  tipo: z.enum(TIPI_AZIONE),
  descrizione: testo(4000).min(1),
  funzione: testoOpz(100),
  owner: ownerIn.nullish(),
  scadenza: data.nullish(),
});
export type NuovaAzioneInput = z.input<typeof NuovaAzioneIn>;
export const ModificaAzioneIn = z
  .object({
    tipo: z.enum(TIPI_AZIONE),
    descrizione: testo(4000).min(1),
    funzione: testoOpz(100),
    owner: ownerIn.nullable(),
    scadenza: data.nullable(),
    stato: z.enum(STATI_AZIONE),
    /** nota dell'aggiornamento, con data automatica */
    nota: testoOpz(4000),
  })
  .partial();
export type ModificaAzioneInput = z.input<typeof ModificaAzioneIn>;

/* ---------- NC ---------- */

export interface NcRigaDto {
  id: string;
  codice: string;
  segnalazione: { id: string; codice: string; cliente: string; severita: Severita };
  tipo: TipoNc;
  fornitore: FornitoreBreveDto | null;
  fornitoreTesto: string | null;
  funzione: string | null;
  fase: FaseNc;
  stato: StatoNc;
  owner: OwnerDto;
  scadenza: string | null;
  scaduta: boolean;
}
export interface NcDto extends NcRigaDto {
  d1: string | null;
  d2: string | null;
  d3: string | null;
  d4: string | null;
  d5: string | null;
  d6: string | null;
  d7: string | null;
  d8: string | null;
  d5Owner: OwnerDto;
  d5Scadenza: string | null;
  d5CostoStimato: number | null;
  verificaDopo: string | null;
  esitoVerifica: EsitoVerifica | null;
  pesoRating: boolean;
  costoRilavorazione: number | null;
  costoAereo: number | null;
  costoRimborso: number | null;
  costoSelezione: number | null;
  costoTotale: number | null;
  chiusaIl: string | null;
  /** altri casi dello stesso fornitore (scheda NC, riquadro fornitore) */
  altriCasiFornitore: { nc: number; claim: number } | null;
  /** può chiudere adesso? (D5 compilata e verifica efficace) */
  chiudibile: boolean;
}

export const FiltriNcIn = z.object({
  q: z.string().trim().max(200).optional(),
  tipo: z.enum(TIPI_NC).optional(),
  fase: z.enum(FASI_NC).optional(),
  stato: z.enum(["aperta", "chiusa"]).optional(),
  senzaOwner: z.coerce.boolean().optional(),
  scadute: z.coerce.boolean().optional(),
  severita: z.enum(SEVERITA).optional(),
  fornitoreId: z.string().optional(),
  mie: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FiltriNc = z.infer<typeof FiltriNcIn>;

/** Lavoro sull'8D (qualità, admin). */
export const ModificaNcIn = z
  .object({
    fase: z.enum(FASI_NC),
    d1: testoOpz(),
    d2: testoOpz(),
    d3: testoOpz(),
    d4: testoOpz(),
    d5: testoOpz(),
    d6: testoOpz(),
    d7: testoOpz(),
    d8: testoOpz(),
    owner: ownerIn,
    funzione: testoOpz(100),
    scadenza: data.nullish(),
    fornitoreId: z.string().min(1).nullish(),
    d5Owner: ownerIn,
    d5Scadenza: data.nullish(),
    d5CostoStimato: importo,
    verificaDopo: testoOpz(60),
    esitoVerifica: z.enum(ESITI_VERIFICA).nullish(),
    pesoRating: z.boolean(),
  })
  .partial();
export type ModificaNcInput = z.input<typeof ModificaNcIn>;

/** Costo della non qualità (qualità, amministrazione, admin). EUR. */
export const CostiNcIn = z
  .object({ costoRilavorazione: importo, costoAereo: importo, costoRimborso: importo, costoSelezione: importo })
  .partial();
export type CostiNcInput = z.input<typeof CostiNcIn>;

export const ChiudiNcIn = z.object({ nota: testoOpz(2000) });
export const RiapriNcIn = z.object({ motivo: testo(2000).min(1) });

/* ---------- claim ---------- */

export interface ClaimRigaDto {
  id: string;
  codice: string;
  fornitore: FornitoreBreveDto | null;
  fornitoreTesto: string;
  brand: string | null;
  problema: string | null;
  risoluzione: string | null;
  importo: number | null;
  valuta: Valuta;
  importoEur: number | null;
  stato: StatoClaim;
  statoOriginale: string | null;
  bloccoPagamento: boolean | null;
  segnalazione: { id: string; codice: string; cliente: string } | null;
  /** collegamento proposto e in attesa (proposta da approvare) */
  segnalazioneProposta: { codice: string; daVerificare: boolean; proposta: PropostaBreveDto } | null;
}
export interface ClaimDto extends ClaimRigaDto {
  modalitaRecupero: string | null;
  importoRecuperato: number | null;
  note: string | null;
  aggiornamenti: AggiornamentoDto[];
  segnalazioneDettaglio: { id: string; codice: string; cliente: string; progetto: string | null; severita: Severita; stato: StatoSegnalazione } | null;
}
export interface ClaimElencoDto {
  righe: ClaimRigaDto[];
  totale: number;
  kpi: { aperti: number; valoreUsd: number; valoreEur: number; collegati: number; collegamentiProposti: number; pagamentiBloccati: number };
}

export const FiltriClaimIn = z.object({
  q: z.string().trim().max(200).optional(),
  stato: z.enum(STATI_CLAIM).optional(),
  fornitoreId: z.string().optional(),
  aperti: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FiltriClaim = z.infer<typeof FiltriClaimIn>;

export const NuovoClaimIn = z.object({
  fornitoreId: z.string().min(1).nullish(),
  fornitoreTesto: testo(200).min(1),
  brand: testoOpz(200),
  problema: testo(2000).min(1),
  risoluzione: testoOpz(2000),
  importo: importo,
  valuta: z.enum(VALUTE).default("EUR"),
  ncId: z.string().min(1).nullish(),
});
export type NuovoClaimInput = z.input<typeof NuovoClaimIn>;

export const ModificaClaimIn = z
  .object({
    fornitoreId: z.string().min(1).nullish(),
    fornitoreTesto: testo(200).min(1),
    brand: testoOpz(200),
    problema: testoOpz(2000),
    risoluzione: testoOpz(2000),
    importo: importo,
    valuta: z.enum(VALUTE),
    stato: z.enum(STATI_CLAIM),
    modalitaRecupero: testoOpz(100),
    importoRecuperato: importo,
    bloccoPagamento: z.boolean().nullable(),
    note: testoOpz(2000),
  })
  .partial();
export type ModificaClaimInput = z.input<typeof ModificaClaimIn>;

/* ---------- proposte ---------- */

export const ContenutoClassificazione = z.object({
  tipoSegnalazione: testoOpz(100),
  tipi: z.array(z.enum(TIPI_CLASSIFICAZIONE)).max(5).default([]),
  causaCliente: testoOpz(100),
  causaChi: testoOpz(100),
  fornitoreId: z.string().min(1).nullish(),
  fornitoreTesto: testoOpz(200),
});
export const ContenutoCollegamento = z.object({
  claimId: z.string().min(1),
  segnalazioneId: z.string().min(1),
  daVerificare: z.boolean().default(false),
});
export const ContenutoNuovaVoce = z.object({ lista: z.enum(LISTE), valore: testo(100).min(1) });
export type ContenutoClassificazioneT = z.infer<typeof ContenutoClassificazione>;
export type ContenutoCollegamentoT = z.infer<typeof ContenutoCollegamento>;
export type ContenutoNuovaVoceT = z.infer<typeof ContenutoNuovaVoce>;

export const NuovaPropostaIn = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("classificazione"), segnalazioneId: z.string().min(1), contenuto: ContenutoClassificazione, nota: testoOpz(1000) }),
  z.object({ tipo: z.literal("collegamento_claim"), contenuto: ContenutoCollegamento, nota: testoOpz(1000) }),
  z.object({ tipo: z.literal("nuova_voce"), contenuto: ContenutoNuovaVoce, nota: testoOpz(1000) }),
]);
export type NuovaPropostaInput = z.input<typeof NuovaPropostaIn>;

export interface PropostaRigaDto {
  id: string;
  codice: string;
  tipo: TipoProposta;
  /** oggetto leggibile: «RF-007», «CL-04 → RF-007», «Cause · chi l'ha causato» */
  oggetto: string;
  /** link dell'oggetto per la UI */
  segnalazioneId: string | null;
  claimId: string | null;
  /** descrizione leggibile di cosa propone */
  descrizione: string;
  contenuto: ContenutoClassificazioneT | ContenutoCollegamentoT | ContenutoNuovaVoceT;
  nota: string | null;
  propostaDa: string;
  propostaIl: string;
  stato: StatoProposta;
  decisaDa: string | null;
  decisaIl: string | null;
  notaDecisione: string | null;
  /** contenuto approvato se diverso dal proposto (Modificata e approvata) */
  contenutoApprovato: ContenutoClassificazioneT | ContenutoCollegamentoT | ContenutoNuovaVoceT | null;
}
export const FiltriProposteIn = z.object({
  stato: z.enum(STATI_PROPOSTA).optional(),
  tipo: z.enum(TIPI_PROPOSTA).optional(),
  /** "import" = dall'import, "persone" = dalle persone */
  da: z.enum(["import", "persone"]).optional(),
  segnalazioneId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FiltriProposte = z.infer<typeof FiltriProposteIn>;
export const DecisioneIn = z.object({ nota: testoOpz(1000) });
/** Modifica e approva: contenuto nuovo dello stesso tipo. */
export const ModificaApprovaIn = z.object({ contenuto: z.unknown(), nota: testoOpz(1000) });
export const ApprovaPiuIn = z.object({ ids: z.array(z.string().min(1)).min(1).max(200), nota: testoOpz(1000) });
export interface EsitoDecisioniDto {
  approvate: string[];
  errori: { id: string; codice: string; messaggio: string }[];
  ncAperte: string[];
}

/* ---------- home, cruscotto, report, ricerca ---------- */

export interface SezioneDto {
  chiave: string;
  sigla: string;
  stato: "attiva" | "in_arrivo";
  percorso: string | null;
  nome: string;
  descrizione: string;
}
export interface DaFareDto {
  tipo: "proposte" | "nc_senza_owner" | "nc_scaduta" | "nc_mia" | "rf_da_aggiornare" | "azione_mia" | "azione_scaduta" | "pro09_ritardo";
  testo: string;
  link: string;
  conteggio?: number;
  codice?: string;
}
export interface HomeDto {
  sezioni: SezioneDto[];
  kpi: {
    /** null per chi non vede i red flag (solo ruoli NC interne) */
    rfAperti: number | null;
    rfCritici: number | null;
    /** null per chi non vede le NC dei red flag */
    ncSenzaOwner: number | null;
    ncTotali: number | null;
    /** null per chi non vede i red flag */
    valoreARischio: number | null;
    /** null per chi non vede i claim */
    claimAperti: number | null;
    claimUsd: number | null;
    claimEur: number | null;
    daFare: number;
  };
  daFare: DaFareDto[];
  novita: SegnalazioneRigaDto[];
}
export interface CruscottoDto {
  kpi: {
    aperti: number;
    urgenti: number;
    critici: number;
    criticiDelta: number | null;
    valoreARischio: number;
    senzaValore: number;
    persi: number;
    valorePersi: number;
    costoNonQualita: number | null;
    /** PRO 09: aggiornamento al cliente in ritardo */
    inRitardo: number;
    azioniAperte: number;
    azioniScadute: number;
  };
  perSeverita: { severita: Severita; n: number }[];
  /** tipo di segnalazione delle aperte: scritto sulla segnalazione, altrimenti proposto */
  perTipo: { tipo: string | null; n: number; proposti: number }[];
  /** dalle cause classificate («chi l'ha causato»), non scritti a mano */
  temi: { causa: string; n: number; clienti: string[] }[];
  fornitori: { fornitore: FornitoreBreveDto | null; nome: string; nc: number; claim: number | null }[];
  andamento: { settimana: string; aperti: number }[];
}
export interface ReportDto {
  settimana: string;
  generatoIl: string;
  aperti: { totale: number; CRITICAL: number; HIGH: number; MEDIUM: number; LOW: number };
  nuove: SegnalazioneRigaDto[];
  aggiornate: SegnalazioneRigaDto[];
  avanzamento: { ncAperte: number; ncSenzaOwner: number; azioniScadute: number; chiuseSettimana: number; claimAperti: number | null };
  temi: { causa: string; n: number; clienti: string[] }[];
  /** testo della mail, plain text */
  testoMail: string;
  /** «Crea bozza in Outlook»: fuori dalla notte */
  bozzaOutlook: { disponibile: false; nota: string };
}
export interface RisultatoRicercaDto {
  tipo: "segnalazione" | "nc" | "claim" | "fornitore";
  id: string;
  codice: string | null;
  titolo: string;
  sottotitolo: string | null;
  link: string;
}

/* ---------- admin ---------- */

export interface UtenteDto {
  id: string;
  email: string;
  nome: string;
  ruoli: RuoloPortale[];
  stato: "attivo" | "disattivato";
  ultimoAccesso: string | null;
}
export interface AccessiDto {
  /** un ruolo del portale per riga, raggruppati per sezione; `alias` = app role storici riconosciuti come questo */
  ruoli: { ruolo: RuoloPortale; sezione: SezioneRuoli; appRole: string; alias: string[]; descrizione: string; utenti: UtenteBreveDto[] }[];
  utenti: UtenteDto[];
  richieste: RichiestaAccessoDto[];
  ssoAttivo: boolean;
}
export interface RichiestaAccessoDto {
  id: string;
  email: string;
  nome: string;
  motivo: string | null;
  stato: "aperta" | "gestita" | "rifiutata";
  at: string;
}
export const RichiestaAccessoIn = z.object({ motivo: testoOpz(1000) });
export const GestisciRichiestaIn = z.object({ stato: z.enum(["gestita", "rifiutata"]) });
export const VoceListaIn = z.object({ lista: z.enum(LISTE), valore: testo(100).min(1), ordine: z.number().int().optional(), attiva: z.boolean().optional() });
export const ModificaVoceListaIn = z.object({ valore: testo(100).min(1), ordine: z.number().int(), attiva: z.boolean() }).partial();
export const CriteriIn = z.object({ criteri: z.array(z.object({ severita: z.enum(SEVERITA), testo: testo(500).min(1) })).min(1).max(4) });
export const CambioIn = z.object({ cambioUsdEur: z.number().gt(0).lt(10) });
export interface RegistroRigaDto {
  id: string;
  at: string;
  utente: string;
  azione: string;
  entita: string;
  entitaId: string | null;
  dettaglio: string | null;
}
export interface EsitoImportDto {
  dryRun: boolean;
  tipo: "redflag" | "claim" | "vendorhub" | "proposte";
  creati: number;
  aggiornati: number;
  invariati: number;
  proposte: number;
  avvisi: string[];
}

// ── Casi Salesforce (prova, QA26-F9.3): copia dei casi 2026 nello schema sf_staging, solo admin, sola lettura ──
export const FiltriSfCasiIn = z.object({
  /** id dell'account o del brand Salesforce (dalle opzioni di /admin/sf/stato) */
  cliente: z.string().trim().max(20).optional(),
  stato: z.string().trim().max(30).optional(),
  recordType: z.string().trim().max(40).optional(),
  /** numero del caso o testo dell'oggetto */
  q: z.string().trim().max(100).optional(),
});
export type FiltriSfCasi = z.infer<typeof FiltriSfCasiIn>;
export interface SfOpzioneDto {
  value: string;
  label: string;
  casi: number;
}
export interface SfStatoDto {
  /** false: schema sf_staging non caricato in questo ambiente */
  attivo: boolean;
  caricatoIl: string | null;
  fonte: string | null;
  conteggi: { casi: number; posizioni: number; storia: number };
  clienti: SfOpzioneDto[];
  stati: SfOpzioneDto[];
  recordType: SfOpzioneDto[];
}
export interface SfCasoBreveDto {
  id: string;
  numero: string;
  recordType: string | null;
  stato: string | null;
  priorita: string | null;
  origine: string | null;
  account: string | null;
  brand: string | null;
  oggetto: string | null;
  apertoIl: string | null;
  chiusoIl: string | null;
  responsabilita: string | null;
  risoluzione: string | null;
  importoClaim: number | null;
  valuta: string | null;
  posizioni: number;
}
export interface SfPosizioneDto {
  id: string;
  nome: string;
  ordine: string | null;
  posizioneOrdine: string | null;
  consegnaIl: string | null;
  brand: string | null;
  categoria: string | null;
  famiglia: string | null;
  prodotto: string | null;
  testo: string | null;
  materiale: string | null;
  produzione: string | null;
  qtaConsegnata: number | null;
  qtaIspezionata: number | null;
  qtaDifettosa: number | null;
  percDifettosa: number | null;
  qtaMancante: number | null;
  qtaSostituita: number | null;
  prezzoUnitario: number | null;
  importoClaim: number | null;
  deltaNotaCredito: number | null;
  valuta: string | null;
  prezzoErrato: boolean | null;
}
export interface SfStoriaDto {
  id: string;
  at: string;
  campo: string;
  da: string | null;
  a: string | null;
}
export interface SfRedFlagDto {
  id: string;
  codice: string;
  cliente: string;
  stato: StatoSegnalazione;
  severita: Severita;
  /** numero: il caso è citato nella fonte della segnalazione; nome: stesso cliente o brand per nome (proposta) */
  motivo: "numero" | "nome";
}
export interface SfCasoDto extends SfCasoBreveDto {
  sfUrl: string | null;
  descrizione: string | null;
  causa: string | null;
  tipoClaim: string | null;
  storiaClaim: string | null;
  azione: string | null;
  commentoRisoluzione: string | null;
  ufficioRisoluzione: string | null;
  reparto: string | null;
  prodottoNonConforme: string | null;
  approvazioneNc: boolean | null;
  statoApprovazione: string | null;
  statoApprovazioneClaim: string | null;
  richiestaNotaCredito: boolean | null;
  richiestaReso: boolean | null;
  ritardoProduzione: boolean | null;
  valoreMerce: number | null;
  deltaNoteCredito: number | null;
  valutaCosti: string | null;
  codiceSapCliente: string | null;
  businessUnit: string | null;
  posizioniDettaglio: SfPosizioneDto[];
  storia: SfStoriaDto[];
  redFlag: SfRedFlagDto[];
}
